import type { DiGraph } from "../graph/digraph.js";
import { ancestors, descendants } from "../graph/dagReach.js";
import { updateAfterAttempt } from "../elo.js";
import type { EloRating } from "../types.js";

export type MasteryState = {
  /** Per-concept “skill” on the Elo scale (same numbers as the global player, default 1200). */
  byConcept: Record<string, EloRating>;
  defaultRating: EloRating;
};

const DEFAULT = 1200;
const BENCH = 1200; // virtual “target mastery” to push toward

/**
 * @param conceptIds — usually `CURRICULUM_NODE_IDS` from the curriculum.
 */
export function createMasteryState(
  conceptIds: readonly string[],
  defaultRating: EloRating = DEFAULT
): MasteryState {
  const byConcept: Record<string, EloRating> = {};
  for (const c of conceptIds) {
    byConcept[c] = defaultRating;
  }
  return { byConcept, defaultRating };
}

/**
 * Elo one-step for the local concept, then:
 * - on **correct** — small **positive** nudge to **ancestors** (prereq reinforcement);
 * - on **wrong** — small **negative** nudge to **descendants** (downstream is shakier).
 *
 * `weight ∈ [0, 1]` (clamped) scales the K-factor on the concept push, which in turn
 * scales the propagation to ancestors/descendants (since they use `share * delta`).
 * Default `1` keeps every existing caller behaving identically.
 */
export function updateMasteryAfterAttempt(
  g: DiGraph,
  state: MasteryState,
  conceptId: string,
  correct: boolean,
  weight: number = 1,
  options: { kConcept?: number; ancestorShare?: number; descendantShare?: number } = {}
): MasteryState {
  const w = Math.max(0, Math.min(1, weight));
  const kConcept = options.kConcept ?? 24;
  const ancestorShare = options.ancestorShare ?? 0.12;
  const descendantShare = options.descendantShare ?? 0.1;
  const old = state.byConcept[conceptId] ?? state.defaultRating;
  const { player: rNew } = updateAfterAttempt(old, BENCH, correct, kConcept * w);
  const delta = rNew - old;
  const next: MasteryState = {
    defaultRating: state.defaultRating,
    byConcept: { ...state.byConcept, [conceptId]: rNew },
  };
  if (Math.abs(delta) < 1e-9) return next;
  if (correct && delta > 0) {
    const anc = ancestors(g, conceptId);
    for (const a of anc) {
      if (a === conceptId) continue;
      const cur = next.byConcept[a] ?? state.defaultRating;
      const bump = ancestorShare * delta;
      next.byConcept[a] = round(cur + bump);
    }
  }
  if (!correct && delta < 0) {
    const des = descendants(g, conceptId);
    for (const d of des) {
      if (d === conceptId) continue;
      const cur = next.byConcept[d] ?? state.defaultRating;
      const bump = descendantShare * delta; // negative
      next.byConcept[d] = round(cur + bump);
    }
  }
  return next;
}

/**
 * Blend for graph selection: global session skill vs per-concept skill.
 * Higher `blend` — trust concept mastery more when picking difficulty.
 */
export function effectivePlayerForConcept(
  globalRating: EloRating,
  conceptId: string,
  state: MasteryState,
  blend = 0.45
): EloRating {
  const local = state.byConcept[conceptId] ?? state.defaultRating;
  return (1 - blend) * globalRating + blend * local;
}

function round(x: number): EloRating {
  return Math.round(x * 10) / 10;
}
